import * as React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import styles from './index.module.css';

import { Button, Size } from '../Button';

import cameraIcon from '../../assets/camera-icon-small.svg';
import { useState } from 'react';
import { Side } from '../../utils/enum';
import { onKeyPress } from '../../utils/keypress';

///////////////////////////////////////////////////
//////TYPES
interface Props {
  label: string;
  side: Side;
  fileChanged: (file: File, side: Side) => void;
  preview?: string;
  handleUploadClick?: () => void;
}

const acceptableExtensions = [
  'image/jpg',
  'image/png',
  'image/jpeg',
  'application/pdf',
];

///////////////////////////////////////////////////
//////COMPONENT
export function ImageUpload(props: Props): JSX.Element {
  const { label, preview, side, handleUploadClick } = props;
  const { t } = useTranslation();
  const [inputId] = useState(() => _.uniqueId('image-input'));

  const triggerRef = React.createRef<HTMLInputElement>();

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = event.target.files;
    if (
      !files ||
      files.length === 0 ||
      !acceptableExtensions.includes(files[0].type)
    ) {
      return null;
    }

    const file = files[0];
    if (file) {
      props.fileChanged(file, side);
    }
  };

  let uploadElements: JSX.Element;
  if (!preview) {
    uploadElements = (
      <div
        className={styles.ImageCardUploadInside}
        onClick={handleUploadClick}
        role="button"
      >
        <div className={styles.LabelCentering}>
          <img src={cameraIcon} alt="camera" />
          <label htmlFor={inputId} key={label}>
            {t(label)}
          </label>
        </div>
      </div>
    );
  } else {
    uploadElements = (
      <Button
        className={styles.ReUploadIcon}
        fontSize=".8rem"
        invertColors
        size={Size.Small}
        type="button"
        children={t('Change')}
        onClick={() => triggerRef.current!.click()}
      />
    );
  }

  return (
    <label
      className={styles.ImageUploadCard}
      htmlFor={inputId}
      tabIndex={0}
      onKeyDown={(e) => {
        onKeyPress({ e, onConfirm: () => triggerRef.current?.click() });
      }}
    >
      <input
        id={inputId}
        ref={triggerRef}
        type={'file'}
        accept={acceptableExtensions.join(', ')}
        onChange={onChange}
      />
      {!preview ? null : (
        <img
          className={styles.Preview}
          src={preview}
          alt={`${t('Change')} ${t(label)}`}
          role="button"
        />
      )}
      {uploadElements}
    </label>
  );
}
